/**
 * App shell: Telegram theme, auth bootstrap, routing frame.
 * Sprint 1 — TZ §7, §8, §10.
 */
import { lazy, Suspense, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

import { hasSession, loginWithLocalTestUser, loginWithTelegram, type AuthUser } from "@/api/auth";
import { getStoredToken, clearStoredToken } from "@/api/client";
import { fetchMyProfile } from "@/api/users";
import { needsLegalAcceptance } from "@/features/legal/legalState";
import { LegalLinks } from "@/features/legal/components/LegalLinks";
import { EmailLoginForm } from "@/components/EmailLoginForm";
import { TelegramBrowserLogin } from "@/components/TelegramBrowserLogin";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { PageSkeleton } from "@/components/ui/PageSkeleton";
import { ToastHost } from "@/components/ui/ToastHost";
import { BetaPlusNotice } from "@/features/subscription/components/BetaPlusNotice";
import { useTelegramExitGesture } from "@/hooks/useTelegramExitGesture";
import { trackEvent } from "@/lib/analytics";
import { isUnauthorizedBrowserSession } from "@/lib/browserSession";
import {
  getStartParam,
  initTelegramAppWhenSdkLoads,
  isTelegramEnvironment,
  pathFromStartParam,
} from "@/lib/telegram";
import { applyProfileRefresh } from "@/lib/profileRefresh";
import { subscribeReadCacheUpdates } from "@/lib/readCacheUpdates";
import { useUserStore } from "@/store/userStore";
import { isOnline } from "@/utils/network";
import {
  cacheUserProfile,
  clearCachedUserProfile,
  readCachedUserProfile,
} from "@/utils/profileCache";
import { toUserMessage } from "@/utils/errors";
import { claimInviteStartParam, rememberPendingInvite } from "@/utils/pendingInvite";

const OfflineBanner = lazy(() =>
  import("@/components/OfflineBanner").then((module) => ({ default: module.OfflineBanner })),
);

const LegalConsentGate = lazy(() => import("@/features/legal/components/LegalConsentGate").then((module) => ({ default: module.LegalConsentGate })));

const ReleaseSummaryOverlay = lazy(() => import("@/features/notifications/components/ReleaseSummaryOverlay").then(module => ({ default: module.ReleaseSummaryOverlay })));

export function Shell() {
  useTelegramExitGesture();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthLoading = useUserStore((s) => s.isAuthLoading);
  const authError = useUserStore((s) => s.authError);
  const user = useUserStore((s) => s.user);
  const userId = user?.id;
  const setUser = useUserStore((s) => s.setUser);
  const setAuthLoading = useUserStore((s) => s.setAuthLoading);
  const setAuthError = useUserStore((s) => s.setAuthError);

  useEffect(() => {
    const stopTelegramSdkListener = initTelegramAppWhenSdkLoads();
    trackEvent("web_app_opened", {
      start_param: getStartParam() || null,
      online: isOnline(),
    });
    let cancelled = false;
    let bootstrapInFlight = false;
    let retryAfterFailure = false;
    let retryQueued = false;

    async function bootstrapAuth() {
      if (bootstrapInFlight || cancelled) return;
      bootstrapInFlight = true;
      setAuthError(null);
      setAuthLoading(true);
      try {
        const start = getStartParam();
        if (start && !cancelled) {
          const inviteToken = start.startsWith("i_") ? start.slice(2) : null;
          const shouldHandleInvite = inviteToken ? claimInviteStartParam(inviteToken) : true;
          const target = shouldHandleInvite ? pathFromStartParam(start) : null;
          if (target && target !== location.pathname + location.search) {
            // ActiveWorkout can restore from IndexedDB or fetch a server-only session.
            navigate(target);
          }
        }

        if (!isTelegramEnvironment()) {
          // Browser outside Telegram: restore JWT or show email OTP form.
          if (!hasSession()) {
            if (!cancelled) {
              setUser(null);
              setAuthLoading(false);
            }
            return;
          }

          const cachedUser = readCachedUserProfile();
          if (cachedUser && !cancelled) {
            // Render the previous verified session immediately. The server refresh
            // below updates it without blocking browser/PWA startup.
            setUser(cachedUser);
            setAuthLoading(false);
          }
          if (!isOnline()) {
            if (!cachedUser && !cancelled) {
              setUser(null);
              setAuthLoading(false);
            }
            return;
          }

          const requestedToken = getStoredToken();
          try {
            const profile = await fetchMyProfile(8_000);
            if (!cancelled) {
              applyProfileRefresh(profile, requestedToken);
              setAuthLoading(false);
            }
          } catch (error) {
            if ((getStoredToken() === requestedToken || !getStoredToken()) && isUnauthorizedBrowserSession(error)) {
              clearStoredToken();
              clearCachedUserProfile();
              if (!cancelled) {
                setUser(null);
                setAuthLoading(false);
              }
            } else if (!cachedUser) {
              throw error;
            }
          }
          return;
        }

        const result = await loginWithTelegram();
        if (!cancelled) {
          retryAfterFailure = false;
          retryQueued = false;
          setUser(result.user);
          cacheUserProfile(result.user);
          setAuthLoading(false);
        }
      } catch (error) {
        retryAfterFailure = true;
        if (!cancelled) {
          const message = toUserMessage(error, "Не удалось войти в приложение");
          setAuthError(message);
          setAuthLoading(false);
        }
      } finally {
        bootstrapInFlight = false;
        if (retryQueued && retryAfterFailure && isOnline() && !cancelled) {
          retryQueued = false;
          queueMicrotask(() => void bootstrapAuth());
        }
      }
    }

    void bootstrapAuth();
    const retryBootstrap = () => {
      if (!retryAfterFailure || !isOnline()) return;
      if (bootstrapInFlight) {
        retryQueued = true;
        return;
      }
      retryQueued = false;
      void bootstrapAuth();
    };
    const retryWhenVisible = () => {
      if (document.visibilityState === "visible") retryBootstrap();
    };
    // A Funnel outage does not change navigator.onLine on the phone, so an
    // "online" event alone cannot recover a failed Telegram authorization.
    window.addEventListener("online", retryBootstrap);
    window.addEventListener("focus", retryBootstrap);
    window.addEventListener("pageshow", retryBootstrap);
    document.addEventListener("visibilitychange", retryWhenVisible);
    const retryTimer = window.setInterval(retryBootstrap, 10_000);
    return () => {
      cancelled = true;
      stopTelegramSdkListener();
      window.removeEventListener("online", retryBootstrap);
      window.removeEventListener("focus", retryBootstrap);
      window.removeEventListener("pageshow", retryBootstrap);
      document.removeEventListener("visibilitychange", retryWhenVisible);
      window.clearInterval(retryTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, setAuthError, setAuthLoading, setUser]);

  useEffect(() => {
    if (location.pathname !== "/invite") return;
    const token = new URLSearchParams(location.search).get("token");
    if (token) rememberPendingInvite(token);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    let stopSync: (() => void) | null = null;
    void import("@/db/syncQueue").then((syncQueue) => {
      if (cancelled) return;
      stopSync = syncQueue.startSyncListeners(userId);
    });
    return () => {
      cancelled = true;
      stopSync?.();
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    void import("@/lib/sessionRestore").then(async (sessionRestore) => {
      if (cancelled) return;
      if (!location.pathname.startsWith("/workouts/active/")) {
        const session = await sessionRestore.findResumableSession(userId);
        if (session && !cancelled) {
          await sessionRestore.restoreSessionIntoStore(session, userId);
        }
      }
    });
    return () => {
      cancelled = true;
    };
  }, [location.pathname, userId]);

  useEffect(() => {
    if (isAuthLoading || authError || !user) return;
    if (needsLegalAcceptance(user) || user.onboarding_completed) return;
    if (location.pathname.startsWith("/onboarding")) return;
    navigate("/onboarding", { replace: true });
  }, [authError, isAuthLoading, location.pathname, navigate, user]);

  useEffect(() => {
    if (isTelegramEnvironment() || !user || !hasSession()) return;
    const verifyAfterReconnect = async () => {
      if (!isOnline()) return;
      const requestedToken = getStoredToken();
      try {
        const profile = await fetchMyProfile(8_000);
        applyProfileRefresh(profile, requestedToken);
      } catch (error) {
        if ((getStoredToken() === requestedToken || !getStoredToken()) && isUnauthorizedBrowserSession(error)) {
          clearStoredToken();
          clearCachedUserProfile();
          setUser(null);
        }
        // Temporary failures keep the cached context; server actions remain unavailable.
      }
    };
    window.addEventListener("online", verifyAfterReconnect);
    return () => window.removeEventListener("online", verifyAfterReconnect);
  }, [setUser, user]);

  useEffect(() => {
    if (!user || !hasSession()) return;
    let refreshing = false;
    const refreshSubscription = async () => {
      if (refreshing || !isOnline()) return;
      refreshing = true;
      const requestedToken = getStoredToken();
      try {
        const profile = await fetchMyProfile(8_000);
        applyProfileRefresh(profile, requestedToken);
      } catch {
        // The original request still shows the feature gate. Keep the session alive.
      } finally {
        refreshing = false;
      }
    };
    window.addEventListener("fitness:plus-required", refreshSubscription);
    const stopRefresh = subscribeReadCacheUpdates(user.id, ["/users/me"], () => { void refreshSubscription(); });
    return () => {
      window.removeEventListener("fitness:plus-required", refreshSubscription);
      stopRefresh();
    };
  }, [setUser, user]);

  const requiresLegal = user ? needsLegalAcceptance(user) : false;

  const isFocusedFlow =
    location.pathname.startsWith("/onboarding") ||
    location.pathname.startsWith("/workouts/active/");

  const completeBrowserLogin = (authenticatedUser: AuthUser) => {
    setUser(authenticatedUser);
    cacheUserProfile(authenticatedUser);
    setAuthError(null);
  };

  const canUseLocalTestLogin = import.meta.env.DEV && ["127.0.0.1", "localhost"].includes(window.location.hostname);

  async function completeLocalTestLogin() {
    setAuthError(null);
    setAuthLoading(true);
    try {
      const result = await loginWithLocalTestUser();
      completeBrowserLogin(result.user);
    } catch (error) {
      setAuthError(toUserMessage(error, "Не удалось открыть тестовый аккаунт"));
    } finally {
      setAuthLoading(false);
    }
  }

  return (
    <div className="app-shell min-h-screen bg-transparent text-tg-text">
      <div
        className={`mx-auto min-h-screen px-4 pt-[calc(1rem+env(safe-area-inset-top))] ${isFocusedFlow ? "max-w-lg pb-[calc(1.5rem+env(safe-area-inset-bottom))]" : "max-w-5xl pb-[calc(6rem+env(safe-area-inset-bottom))] lg:pb-8 lg:pt-24"}`}
      >
        {isAuthLoading ? (
          <p className="text-sm text-tg-hint">Авторизация…</p>
        ) : null}

        {!isAuthLoading && authError ? (
          <div className="mb-4 rounded-xl bg-tg-secondary p-3 text-sm">
            <p className="font-medium">Не удалось войти</p>
            <p className="mt-1 text-tg-hint">{authError}</p>
            {isTelegramEnvironment() ? (
              <>
                <p className="mt-2 text-xs text-tg-hint">
                  После смены сети или VPN подождите несколько секунд и повторите вход.
                </p>
                <button
                  type="button"
                  className="mt-3 min-h-11 rounded-xl bg-tg-button px-4 py-2 font-medium text-tg-button-text"
                  onClick={() => window.dispatchEvent(new Event("online"))}
                >
                  Подключиться снова
                </button>
              </>
            ) : null}
          </div>
        ) : null}

        {!isAuthLoading && !isTelegramEnvironment() && !user ? (
          <>
            {canUseLocalTestLogin ? (
              <button
                type="button"
                onClick={() => void completeLocalTestLogin()}
                className="app-button app-secondary-action mb-3 min-h-11 w-full"
              >
                Войти как тестовый пользователь
              </button>
            ) : null}
            <TelegramBrowserLogin onSuccess={completeBrowserLogin} />
            <EmailLoginForm onSuccess={completeBrowserLogin} />
            {import.meta.env.MODE !== "android" ? <Link to="/download" className="my-3 flex min-h-11 items-center justify-center text-sm text-tg-link">Скачать для Android</Link> : null}
            <LegalLinks />
          </>
        ) : null}

        {!isAuthLoading && user ? (
          <Suspense fallback={null}>
            <OfflineBanner />
          </Suspense>
        ) : null}

        {!isAuthLoading && user && !requiresLegal && !isFocusedFlow ? <BetaPlusNotice user={user} /> : null}
        {!isAuthLoading && !authError && user?.onboarding_completed && !requiresLegal && location.pathname === "/" ? <Suspense fallback={null}><ReleaseSummaryOverlay key={user.id} owner={user.id} /></Suspense> : null}

        {!isAuthLoading && user && requiresLegal ? <Suspense fallback={<p>Загружаем документы…</p>}><LegalConsentGate key={user.id} user={user} /></Suspense> : null}
        {!isAuthLoading && !requiresLegal && (user || import.meta.env.DEV) ? <Suspense fallback={<PageSkeleton />}><Outlet /></Suspense> : null}
      </div>
      <ToastHost />
      {!requiresLegal && !isFocusedFlow && (user || import.meta.env.DEV) ? <BottomNavigation /> : null}
    </div>
  );
}
